import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Stack
} from '@mui/material';
import { Edit as EditIcon, Email as EmailIcon, Campaign as CampaignIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../auth';
import { PrayerRequest, prayerRequestService } from '../service/PrayerRequestService';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { baseBackendUrl } from '../service/baseService';

const ManagePrayerRequests: React.FC = () => {
    const { IsAdmin, cookies } = useContext(useAuth);
    const navigate = useNavigate();
    const [requests, setRequests] = useState<PrayerRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Edit Dialog State
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<PrayerRequest | null>(null);

    // Email Dialog State
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [mailingLists, setMailingLists] = useState<string[]>([]);
    const [selectedMailingList, setSelectedMailingList] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');

    useEffect(() => {
        if (IsAdmin && cookies?.UserAuth?.token) {
            fetchRequests();
        }
    }, [IsAdmin, cookies]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const token = cookies?.UserAuth?.token;
            if (token) {
                const data = await prayerRequestService.getAllRequests(token);
                setRequests(data);
            } else {
                setError("Authentication token not found");
            }
        } catch (err) {
            setError('Failed to fetch prayer requests');
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (request: PrayerRequest) => {
        setSelectedRequest({ ...request });
        setEditDialogOpen(true);
    };

    const handleEditSave = async () => {
        if (!selectedRequest || !cookies?.UserAuth?.token) return;
        try {
            await prayerRequestService.updateRequest(selectedRequest, cookies.UserAuth.token);
            setSuccess('Prayer request updated successfully');
            setEditDialogOpen(false);
            fetchRequests();
        } catch (err) {
            setError('Failed to update prayer request');
        }
    };

    const handleEmailClick = async (request: PrayerRequest) => {
        setSelectedRequest(request);
        setEmailSubject(`Prayer Request: ${request.title}`);
        setEmailBody(request.description);

        // Fetch mailing lists
        if (cookies?.UserAuth?.token) {
            try {
                const response = await fetch(`${baseBackendUrl}/api/email/Get-Available-Mailing-Lists`, {
                    headers: { 'Authorization': `Bearer ${cookies.UserAuth.token}` }
                });
                if (response.ok) {
                    setMailingLists(await response.json());
                }
            } catch (err) {
                console.error('Failed to load mailing lists');
            }
        }

        setEmailDialogOpen(true);
    };

    const handleSendEmail = async () => {
        if (!selectedRequest || !selectedMailingList || !cookies?.UserAuth?.token) return;

        try {
            const response = await fetch(`${baseBackendUrl}/api/email/send-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${cookies.UserAuth.token}`
                },
                body: JSON.stringify({
                    mailingListName: selectedMailingList,
                    subject: emailSubject,
                    body: emailBody
                })
            });

            if (!response.ok) throw new Error('Failed to send email');

            setSuccess('Email sent successfully');
            setEmailDialogOpen(false);
        } catch (err) {
            setError('Failed to send email');
        }
    };

    const handleConvertToAnnouncement = (request: PrayerRequest) => {
        navigate('/ManageAnnouncements', {
            state: {
                createFromPrayerRequest: {
                    title: request.title,
                    description: request.description
                }
            }
        });
    };

    const handleDeleteClick = async (id: number) => {
        if (!cookies?.UserAuth?.token) return;
        if (window.confirm('Are you sure you want to delete this prayer request?')) {
            try {
                await prayerRequestService.deleteRequest(id, cookies.UserAuth.token);
                setSuccess('Prayer request deleted successfully');
                fetchRequests();
            } catch (err) {
                setError('Failed to delete prayer request');
            }
        }
    };

    if (!IsAdmin) {
        return <Typography variant="h5" color="error" align="center" sx={{ mt: 4 }}>Access Denied</Typography>;
    }

    return (
        <Box sx={{ p: 3, pt: 10 }}>
            <Typography variant="h4" gutterBottom>Manage Prayer Requests</Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {requests.map((request) => (
                            <TableRow key={request.id}>
                                <TableCell>{format(new Date(request.createdDate), 'MM/dd/yyyy')}</TableCell>
                                <TableCell>{request.title}</TableCell>
                                <TableCell>{request.description}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1}>
                                        <IconButton onClick={() => handleEditClick(request)} title="Edit">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleEmailClick(request)} title="Send Email">
                                            <EmailIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleConvertToAnnouncement(request)} title="Convert to Announcement">
                                            <CampaignIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDeleteClick(request.id)} title="Delete">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Prayer Request</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Title"
                            value={selectedRequest?.title || ''}
                            onChange={(e) => setSelectedRequest(prev => prev ? { ...prev, title: e.target.value } : null)}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            value={selectedRequest?.description || ''}
                            onChange={(e) => setSelectedRequest(prev => prev ? { ...prev, description: e.target.value } : null)}
                            multiline
                            rows={4}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            {/* Email Dialog */}
            <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Send Prayer Request Email</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel>Mailing List</InputLabel>
                            <Select
                                value={selectedMailingList}
                                label="Mailing List"
                                onChange={(e) => setSelectedMailingList(e.target.value)}
                            >
                                {mailingLists.map(list => (
                                    <MenuItem key={list} value={list}>{list}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Subject"
                            value={emailSubject}
                            onChange={(e) => setEmailSubject(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Body"
                            value={emailBody}
                            onChange={(e) => setEmailBody(e.target.value)}
                            multiline
                            rows={4}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendEmail} variant="contained" disabled={!selectedMailingList}>Send</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ManagePrayerRequests;
